/**
 * The branding token registry — the single source of truth for D-02.
 *
 * Every token listed here is:
 *   • settable by the super admin as a PLATFORM DEFAULT (always has a value)
 *   • overridable by an org admin (null = inherit the platform default)
 *   • rendered as a CSS custom property on :root
 *
 * Adding a token means adding one entry here. The API validator, the settings
 * UI, the merge function and the CSS variable output all read from this list,
 * so there is exactly one place to change.
 */
/** Fonts an org may choose. All are available on Google Fonts. */
export const FONT_OPTIONS = [
    "Poppins",
    "Outfit",
    "Inter",
    "Manrope",
    "DM Sans",
    "Plus Jakarta Sans",
    "Work Sans",
    "Rubik",
    "Nunito Sans",
    "Lato",
];
export const SHADOW_LEVELS = ["none", "soft", "medium", "strong"];
export const BORDER_STYLES = ["hairline", "solid", "none"];
export const SIDEBAR_STYLES = ["light", "dark", "brand"];
export const HEADER_STYLES = ["light", "dark", "brand"];
export const SPACING_SCALES = ["compact", "normal", "roomy"];
export const CONTAINER_WIDTHS = ["narrow", "normal", "wide", "full"];
export const HEADING_WEIGHTS = ["500", "600", "700"];
/**
 * Applied to `body`, never to `html`. Changing the ROOT size would rescale
 * every rem-based Tailwind utility — padding, heights, icons — not just text.
 */
export const BASE_FONT_SIZES = ["14px", "15px", "16px", "17px", "18px"];
export const BRANDING_TOKENS = [
    // ── Identity (5) ───────────────────────────────────────────
    {
        key: "appName",
        group: "identity",
        label: "App name",
        type: "text",
        default: "GatiQ Rewards",
        help: "Shown in the browser tab, the sidebar and every email.",
    },
    { key: "logo", group: "identity", label: "Logo", type: "url", default: "" },
    {
        key: "logoDark",
        group: "identity",
        label: "Logo (dark background)",
        type: "url",
        default: "",
        help: "Optional. Used on dark sidebars and headers.",
    },
    { key: "favicon", group: "identity", label: "Favicon", type: "url", default: "" },
    { key: "tagline", group: "identity", label: "Tagline", type: "text", default: "" },
    // ── Brand colors (2) — client specification ────────────────
    {
        key: "color1",
        group: "brand",
        label: "Color 1 — primary",
        type: "color",
        default: "#C54E1B",
        cssVar: "--gq-color-1",
        help: "Buttons, links, active states. Five more colors are derived from this one.",
    },
    {
        key: "color2",
        group: "brand",
        label: "Color 2 — surface",
        type: "color",
        default: "#FAFAFA",
        cssVar: "--gq-color-2",
        help: "Page background.",
    },
    {
        key: "colorPanel",
        group: "brand",
        label: "Sign-in panel",
        type: "color",
        // Empty = derive a deep shade of Color 1. The client specified a two-colour
        // system, so the default stays derived; an organization that wants a
        // contrasting panel (a deep navy, say) can set one here.
        default: "",
        help: "Dark panel behind the sign-in screen. Leave empty to derive it from Color 1.",
    },
    // ── Semantic colors (5) — status, never the accent ─────────
    { key: "colorSuccess", group: "semantic", label: "Success", type: "color", default: "#2E7D4F", cssVar: "--gq-success" },
    { key: "colorWarning", group: "semantic", label: "Warning", type: "color", default: "#B8860B", cssVar: "--gq-warning" },
    { key: "colorError", group: "semantic", label: "Error", type: "color", default: "#B3261E", cssVar: "--gq-error" },
    { key: "colorInfo", group: "semantic", label: "Info", type: "color", default: "#1E5AA8", cssVar: "--gq-info" },
    { key: "colorTextMuted", group: "semantic", label: "Muted text", type: "color", default: "#5C534D", cssVar: "--gq-text-muted" },
    // ── Typography (4) — client specification ──────────────────
    {
        key: "fontTitle",
        group: "typography",
        label: "Headings font",
        type: "font",
        default: "Poppins",
        cssVar: "--gq-font-title",
        options: FONT_OPTIONS,
    },
    {
        key: "fontBody",
        group: "typography",
        label: "Body font",
        type: "font",
        default: "Outfit",
        cssVar: "--gq-font-body",
        options: FONT_OPTIONS,
    },
    {
        key: "baseFontSize",
        group: "typography",
        label: "Base text size",
        type: "enum",
        default: "16px",
        cssVar: "--gq-font-size-base",
        options: BASE_FONT_SIZES,
        help: "Applies to body copy. Headings and controls keep their own sizes.",
    },
    {
        key: "headingWeight",
        group: "typography",
        label: "Heading weight",
        type: "enum",
        default: "600",
        cssVar: "--gq-heading-weight",
        options: HEADING_WEIGHTS,
    },
    // ── Shape & depth (5) ──────────────────────────────────────
    { key: "radiusSm", group: "shape", label: "Small radius", type: "size", default: "6px", cssVar: "--gq-radius-sm" },
    { key: "radiusMd", group: "shape", label: "Medium radius", type: "size", default: "10px", cssVar: "--gq-radius-md" },
    { key: "radiusLg", group: "shape", label: "Large radius", type: "size", default: "16px", cssVar: "--gq-radius-lg" },
    {
        key: "shadowLevel",
        group: "shape",
        label: "Shadow depth",
        type: "enum",
        default: "soft",
        options: SHADOW_LEVELS,
    },
    {
        key: "borderStyle",
        group: "shape",
        label: "Borders",
        type: "enum",
        default: "hairline",
        options: BORDER_STYLES,
    },
    // ── Layout (4) ─────────────────────────────────────────────
    {
        key: "spacingScale",
        group: "layout",
        label: "Spacing",
        type: "enum",
        default: "normal",
        options: SPACING_SCALES,
    },
    {
        key: "sidebarStyle",
        group: "layout",
        label: "Sidebar",
        type: "enum",
        default: "light",
        options: SIDEBAR_STYLES,
    },
    {
        key: "headerStyle",
        group: "layout",
        label: "Header",
        type: "enum",
        default: "light",
        options: HEADER_STYLES,
    },
    {
        key: "containerWidth",
        group: "layout",
        label: "Content width",
        type: "enum",
        // "wide", not "normal". 1200px is a READING width, right for prose and
        // wrong for a workspace: on a 1900px screen it left a third of the window
        // as dead margin beside tables that wanted the room. Anyone who prefers
        // the narrower measure can still choose it here.
        default: "wide",
        options: CONTAINER_WIDTHS,
    },
    // ── Contact (5) ────────────────────────────────────────────
    {
        key: "supportPhone",
        group: "contact",
        label: "Support phone",
        type: "phone",
        // Seeded so the footer is never blank out of the box. The super admin can
        // change it, and any org can override it with their own.
        default: "+91 12345 67890",
        // One number serves both channels. There is no separate WhatsApp field:
        // it was the same number typed twice, and two fields that must agree will
        // eventually disagree.
        help: "Used for both calls and WhatsApp.",
    },
    {
        key: "supportEmail",
        group: "contact",
        label: "Support email",
        type: "email",
        default: "support@gatiq.in",
    },
    {
        key: "website",
        group: "contact",
        label: "Website",
        // `link`, not `url`: `url` means "an uploaded asset" and renders a file
        // picker. A website is typed, not uploaded.
        type: "link",
        default: "",
        help: "e.g. https://example.com",
    },
    { key: "address", group: "contact", label: "Address", type: "text", default: "" },
];
export const BRANDING_TOKEN_KEYS = BRANDING_TOKENS.map((t) => t.key);
export const BRANDING_TOKEN_MAP = Object.fromEntries(BRANDING_TOKENS.map((t) => [t.key, t]));
export const TOKEN_GROUP_LABELS = {
    identity: "Identity",
    brand: "Brand colors",
    semantic: "Status colors",
    typography: "Typography",
    shape: "Shape & depth",
    layout: "Layout",
    contact: "Contact details",
};
/** Color tokens only, for the contrast validator. */
export const COLOR_TOKEN_KEYS = BRANDING_TOKENS.filter((t) => t.type === "color").map((t) => t.key);
//# sourceMappingURL=tokens.js.map